import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import PageHeader from '../components/PageHeader';
import './Contact.css';
import usePageSearchIndex from '../hooks/usePageSearchIndex';

const ContactSOP = () => {
  const { t, isMarathi } = useLanguage();
  const [isLoading, setIsLoading] = useState(true);
  const [officials, setOfficials] = useState([]);
  const [divisionalOfficials, setDivisionalOfficials] = useState([]);
  const [customerSupport, setCustomerSupport] = useState(null);
  const [contactTimings, setContactTimings] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('all');

  const pageHeaderData = {
    title: {
      marathi: 'संपर्क - SOP',
      english: 'Contact Us - SOP'
    },
    subtitle: {
      marathi: 'ग्राहक सहायता, संपर्क निर्देशिका आणि कार्यालयीन वेळ',
      english: 'Customer Support, Contact Directory and Office Hours'
    }
  };

  useEffect(() => {
    const loadContactData = async () => {
      setIsLoading(true);
      
      try {
        const API_BASE = process.env.NODE_ENV === 'production' 
          ? 'https://adminconsole.mlwb.in/api' 
          : 'http://localhost:5000/api';
        const [officialsResponse, supportResponse, divisionalResponse, timingsResponse] = await Promise.all([
          fetch(`${API_BASE}/admin/contact-central`),
          fetch(`${API_BASE}/admin/customer-support`),
          fetch(`${API_BASE}/admin/contact-divisional`),
          fetch(`${API_BASE}/contact-timings`)
        ]);

        const officialsData = officialsResponse.ok ? await officialsResponse.json() : [];
        const supportData = supportResponse.ok ? await supportResponse.json() : [];
        const divisionalData = divisionalResponse.ok ? await divisionalResponse.json() : [];
        const timingsData = timingsResponse.ok ? await timingsResponse.json() : [];

        setOfficials(officialsData);
        setCustomerSupport(supportData[0] || null);
        setDivisionalOfficials(divisionalData);
        setContactTimings(timingsData);

      } catch (error) {
        console.error('Error loading contact data:', error);
        setOfficials([]);
        setDivisionalOfficials([]);
        setCustomerSupport(null);
        setContactTimings([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadContactData();
  }, []);

  // Auto-index Contact SOP page content for search
  usePageSearchIndex({
    mr: ['संपर्क SOP', 'ग्राहक सहायता', 'संपर्क निर्देशिका', 'कार्यालयीन वेळ', 'अधिकारी', 'विभाग'],
    en: ['contact sop', 'customer support', 'contact directory', 'office hours', 'officials', 'department']
  });

  const convertToMarathiNumerals = (number) => {
    const marathiNumerals = ['०', '१', '२', '३', '४', '५', '६', '७', '८', '९'];
    return number.toString().split('').map(digit => marathiNumerals[parseInt(digit)]).join('');
  };

  const generateDivisionalEmail = (official) => {
    const designation = official.designation_english || '';
    const address = official.office_address_english || '';
    
    if (address.includes('Mumbai') && designation.includes('Assistant Welfare Commissioner')) {
      return 'awcmu.mlwb-mh@gov.in';
    } else if (address.includes('Mumbai') && designation.includes('Assistant Accounts Officer')) {
      return 'account.mlwb-mh@gov.in';
    } else if (address.includes('Mumbai') && designation.includes('Labour Welfare Officer')) {
      if (address.includes('Naigaon')) return 'wwomc1.mlwb-mh@gov.in';
      if (address.includes('Worli')) return 'wwomc2.mlwb-mh@gov.in';
      if (address.includes('Andheri')) return 'wwomc3.mlwb-mh@gov.in';
      return 'wwomumbai.mlwb-mh@gov.in';
    } else if (address.includes('Thane')) {
      return 'wwothane.mlwb-mh@gov.in';
    } else if (address.includes('Pune')) {
      if (designation.includes('Assistant Welfare Commissioner')) return 'awcpune.mlwb-mh@gov.in';
      return 'wwopune.mlwb-mh@gov.in';
    } else if (address.includes('Nashik')) {
      if (designation.includes('Assistant Welfare Commissioner')) return 'awcnashik.mlwb-mh@gov.in';
      return 'wwonashik.mlwb-mh@gov.in';
    } else if (address.includes('Aurangabad')) {
      if (designation.includes('Assistant Welfare Commissioner')) return 'awcaurangabad.mlwb-mh@gov.in';
      return 'wwoaurangabad.mlwb-mh@gov.in';
    } else if (address.includes('Nagpur')) {
      if (designation.includes('Assistant Welfare Commissioner')) return 'awcnagpur.mlwb-mh@gov.in';
      return 'wwonagpur.mlwb-mh@gov.in';
    } else if (address.includes('Akola')) {
      if (designation.includes('Assistant Welfare Commissioner')) return 'awcakola.mlwb-mh@gov.in';
      return 'wwoakola.mlwb-mh@gov.in';
    } else if (address.includes('Kolhapur')) {
      return 'wwokolhapur.mlwb-mh@gov.in';
    } else if (address.includes('Solapur')) {
      return 'wwosolapur.mlwb-mh@gov.in';
    } else if (address.includes('Sangli')) {
      return 'wwosangli.mlwb-mh@gov.in';
    } else if (address.includes('Jalgaon')) {
      return 'wwojalgaon.mlwb-mh@gov.in';
    } else if (address.includes('Nanded')) {
      return 'wwonanded.mlwb-mh@gov.in';
    } else if (address.includes('Latur')) {
      return 'awclatur.mlwb-mh@gov.in';
    } else if (address.includes('Chandrapur')) {
      return 'wwochandrapur.mlwb-mh@gov.in';
    } else if (address.includes('Amravati')) {
      return 'wwoamravati.mlwb-mh@gov.in';
    } else if (address.includes('Chiplun')) {
      return 'wwochiplun.mlwb-mh@gov.in';
    }
    
    return 'info.mlwb-mh@gov.in';
  };

  const handlePhoneClick = (phone) => {
    window.open(`tel:${phone.replace(/\D/g, '')}`, '_self');
  };

  const handleEmailClick = (email) => {
    window.open(`mailto:${email}`, '_self');
  };

  const handleKeyPress = (event, action) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      action();
    }
  };

  const filteredOfficials = officials.filter(official => {
    const matchesSearch = 
      official.name_english?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      official.name_marathi?.includes(searchTerm) ||
      official.designation_english?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      official.designation_marathi?.includes(searchTerm) ||
      official.email_english?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      official.email_marathi?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesDepartment = selectedDepartment === 'all' || 
      official.designation_english?.toLowerCase().includes(selectedDepartment.toLowerCase());
    
    return matchesSearch && matchesDepartment;
  });

  const filteredDivisionalOfficials = divisionalOfficials.filter(official => {
    const matchesSearch = 
      official.name_english?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      official.name_marathi?.includes(searchTerm) ||
      official.designation_english?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      official.designation_marathi?.includes(searchTerm) ||
      official.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesDepartment = selectedDepartment === 'all' || 
      official.designation_english?.toLowerCase().includes(selectedDepartment.toLowerCase());
    
    return matchesSearch && matchesDepartment;
  });

  const allOfficials = [...officials, ...divisionalOfficials];
  const departments = [...new Set(allOfficials.map(official => 
    official.designation_english?.split(' ')[0] || 'Other'
  ))];

  if (isLoading) {
    return (
      <div className="contact-loading">
        <div className="loading-spinner"></div>
        <p className="loading-text">{t('loading')}</p>
      </div>
    );
  }

  return (
    <div className="contact">
      <div className="contact-container">
        <PageHeader 
          title={pageHeaderData.title}
          subtitle={pageHeaderData.subtitle}
        />

        {/* Customer Support */}
        <section className="customer-support-section" aria-labelledby="support-title" data-read-section="contact-sop-content">
          <div className="support-card">
            <h2 id="support-title" className="section-title support-title">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" fill="currentColor"/>
              </svg>
              {isMarathi() ? 'ग्राहक सहायता' : 'Customer Support'}
            </h2>
            <div className="support-content">
              <div className="support-details">
                <div className="support-item">
                  <span className="support-label">
                    {isMarathi() ? 'ग्राहक सहायता ईमेल:' : 'Customer Support Email:'}
                  </span>
                  {customerSupport ? (
                    <button 
                      className="support-action"
                      onClick={() => handleEmailClick(customerSupport.support_email_english)}
                      onKeyDown={(e) => handleKeyPress(e, () => handleEmailClick(customerSupport.support_email_english))}
                      aria-label={`${isMarathi() ? 'ईमेल पाठवा' : 'Send Email'} ${customerSupport.support_email_english}`}
                      tabIndex="0"
                    >
                      {isMarathi() ? customerSupport.support_email_marathi : customerSupport.support_email_english}
                    </button>
                  ) : (
                    <span className="support-value">-</span>
                  )}
                </div>
                
                <div className="support-item">
                  <span className="support-label">
                    {isMarathi() ? 'ग्राहक सहायता फोन:' : 'Customer Support Phone:'}
                  </span>
                  {customerSupport ? (
                    <button 
                      className="support-action"
                      onClick={() => handlePhoneClick(customerSupport.support_phone_english)}
                      onKeyDown={(e) => handleKeyPress(e, () => handlePhoneClick(customerSupport.support_phone_english))}
                      aria-label={`${isMarathi() ? 'फोन करा' : 'Call'} ${customerSupport.support_phone_english}`}
                      tabIndex="0"
                    >
                      {isMarathi() ? customerSupport.support_phone_marathi : customerSupport.support_phone_english}
                    </button>
                  ) : (
                    <span className="support-value">-</span>
                  )}
                </div>
                
                <div className="support-item">
                  <span className="support-label">
                    {isMarathi() ? 'कार्यकाळ:' : 'Operating Hours:'}
                  </span>
                  <span className="support-value">
                    {customerSupport ? (
                      isMarathi() ? customerSupport.operating_hours_marathi : customerSupport.operating_hours_english
                    ) : (
                      '-'
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Standard Operating Procedure (SOP) Section */}
        <section className="sop-section" aria-labelledby="sop-title" data-read-section="contact-sop-content">
          <div className="sop-card">
            <h2 id="sop-title" className="section-title sop-title">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M16 13H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M16 17H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M10 9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {isMarathi() ? 'प्रमाणित कार्यप्रणाली (एसओपी) – आमच्याशी संपर्क साधा' : 'Standard Operating Procedure (SOP) – Contact Us'}
            </h2>
            <div className="sop-content">
              <div className="sop-download">
                <a
                  href="https://mlwb.in/react-static-files/pdf/contact-sop/Contact-Us-SOP.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="sop-download-link"
                  aria-label={isMarathi() ? 'प्रमाणित कार्यप्रणाली (एसओपी) PDF डाउनलोड करा' : 'Download Standard Operating Procedure (SOP) PDF'}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="sop-icon">
                    <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M16 13H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M16 17H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M10 9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <div className="sop-download-text">
                    <span className="sop-download-title">
                      {isMarathi() ? 'प्रमाणित कार्यप्रणाली (एसओपी) – आमच्याशी संपर्क साधा' : 'Standard Operating Procedure (SOP) – Contact Us'}
                    </span>
                    <span className="sop-download-subtitle">
                      {isMarathi() ? 'PDF डाउनलोड करा' : 'Download PDF'}
                    </span>
                  </div>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="sop-download-arrow">
                    <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Directory - Hidden */}
        {/* 
        <section className="directory-section" aria-labelledby="directory-title" data-read-section="contact-sop-content">
          <div className="directory-header">
            <h2 id="directory-title" className="section-title">
              {isMarathi() ? 'संपर्क निर्देशिका' : 'Contact Directory'}
            </h2>
            
            <div className="directory-controls">
              <div className="search-box">
                <label htmlFor="official-search" className="sr-only">
                  {isMarathi() ? 'अधिकारी शोधा' : 'Search officials'}
                </label>
                <input
                  id="official-search"
                  type="text"
                  placeholder={isMarathi() ? 'अधिकारी शोधा...' : 'Search officials...'}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                  aria-label={isMarathi() ? 'अधिकारी शोधा' : 'Search officials'}
                  aria-describedby="search-help"
                />
                <span className="search-icon" aria-hidden="true">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" fill="currentColor"/>
                  </svg>
                </span>
                <div id="search-help" className="sr-only">
                  {isMarathi() ? 'नाव, पद, किंवा ईमेल द्वारे शोधा' : 'Search by name, designation, or email'}
                </div>
              </div>

              <label htmlFor="department-filter" className="sr-only">
                {isMarathi() ? 'विभाग निवडा' : 'Select department'}
              </label>
              <select
                id="department-filter"
                value={selectedDepartment} 
                onChange={(e) => setSelectedDepartment(e.target.value)}
                className="department-filter"
                aria-label={isMarathi() ? 'विभाग निवडा' : 'Select department'}
              >
                <option value="all">
                  {isMarathi() ? 'सर्व विभाग' : 'All Departments'}
                </option>
                {departments.map((dept) => (
                  <option key={dept} value={dept.toLowerCase()}>
                    {dept}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="officials-table-container">
            {(filteredOfficials.length > 0 || filteredDivisionalOfficials.length > 0) ? (
              <table className="officials-table" role="table" aria-label={isMarathi() ? 'संपर्क अधिकारी सारणी' : 'Contact Officials Table'}>
                <thead>
                  <tr className="table-main">
                    <td colSpan="6" className="main-cell">
                      <div className="main-content">
                        <div className="main-title">
                          {isMarathi() ? 'महाराष्ट्र कामगार कल्याण मंडळ' : 'Maharashtra Labour Welfare Board'}
                        </div>
                        <div className="main-subtitle">
                          {isMarathi() ? 'मध्यवर्ती कार्यालय, मुंबई' : 'Central Office, Mumbai'}
                        </div>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <th scope="col" className="table-header serial-col">
                      {isMarathi() ? 'अ. क्र.' : 'Sr. No.'}
                    </th>
                    <th scope="col" className="table-header name-col">
                      {isMarathi() ? 'नाव' : 'Name'}
                    </th>
                    <th scope="col" className="table-header designation-col">
                      {isMarathi() ? 'पद' : 'Designation'}
                    </th>
                    <th scope="col" className="table-header address-col">
                      {isMarathi() ? 'कार्यालयाचा पत्ता' : 'Office Address'}
                    </th>
                    <th scope="col" className="table-header phone-col">
                      {isMarathi() ? 'दूरध्वनी क्रमांक' : 'Phone Number'}
                    </th>
                    <th scope="col" className="table-header email-col">
                      {isMarathi() ? 'ई-मेल' : 'E-Mail'}
                    </th>
                  </tr>
                  <tr className="table-subhead">
                    <td colSpan="6" className="subhead-cell">
                      {isMarathi() ? 'मध्यवर्ती कार्यालय, मुंबई' : 'Central Office, Mumbai'}
                    </td>
                  </tr>
                </thead>
                <tbody>
                  {filteredOfficials.map((official, index) => (
                    <tr key={official.id} className={`table-row ${index % 2 === 0 ? 'even-row' : 'odd-row'}`}>
                      <td className="table-cell serial-cell">
                        {isMarathi() ? 
                          (official.serial_number_marathi || convertToMarathiNumerals(index + 1)) : 
                          (official.serial_number_english || index + 1)
                        }
                      </td>
                      <td className="table-cell name-cell">
                        <div className="name-content">
                          <div className="name-text">
                            {isMarathi() ? official.name_marathi : official.name_english}
                          </div>
                        </div>
                      </td>
                      <td className="table-cell designation-cell">
                        <div className="designation-text">
                          {isMarathi() ? official.designation_marathi : official.designation_english}
                        </div>
                      </td>
                      <td className="table-cell address-cell">
                        <div className="address-content">
                          {official.office_address_english ? (
                            <div className="address-text">
                              {isMarathi() ? official.office_address_marathi : official.office_address_english}
                            </div>
                          ) : (
                            <div className="address-text">
                              {isMarathi() ? 'वरीलप्रमाणे' : 'Same as above'}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="table-cell phone-cell">
                        {official.phone_numbers_english ? (
                          <button 
                            className="phone-action"
                            onClick={() => handlePhoneClick(official.phone_numbers_english)}
                            onKeyDown={(e) => handleKeyPress(e, () => handlePhoneClick(official.phone_numbers_english))}
                            aria-label={`${isMarathi() ? 'फोन करा' : 'Call'} ${isMarathi() ? official.phone_numbers_marathi : official.phone_numbers_english}`}
                            tabIndex="0"
                            title={isMarathi() ? 'फोन करा' : 'Click to call'}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" fill="currentColor"/>
                            </svg>
                            <span className="phone-text">
                              {isMarathi() ? official.phone_numbers_marathi : official.phone_numbers_english}
                            </span>
                          </button>
                        ) : (
                          <span className="no-data">-</span>
                        )}
                      </td>
                      <td className="table-cell email-cell">
                        {(() => {
                          const email = isMarathi() ? official.email_marathi : official.email_english;
                          return email ? (
                            <button
                              className="email-action"
                              onClick={() => handleEmailClick(email)}
                              onKeyDown={(e) => handleKeyPress(e, () => handleEmailClick(email))}
                              aria-label={`${isMarathi() ? 'ईमेल पाठवा' : 'Send Email'} ${email}`}
                              tabIndex="0"
                              title={isMarathi() ? 'ईमेल पाठवा' : 'Click to email'}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" fill="currentColor"/>
                              </svg>
                              <span className="email-text">{email}</span>
                            </button>
                          ) : (
                            <span className="no-data">-</span>
                          );
                        })()}
                      </td>
                    </tr>
                  ))}
                  
                  {filteredDivisionalOfficials.length > 0 && (
                    <>
                      <tr className="table-divisional-main">
                        <td colSpan="6" className="divisional-main-cell">
                          <div className="divisional-main-content">
                            <div className="divisional-main-title">
                              {isMarathi() ? 'महाराष्ट्र कामगार कल्याण मंडळ' : 'Maharashtra Labour Welfare Board'}
                            </div>
                            <div className="divisional-main-subtitle">
                              {isMarathi() ? 'विभागीय / गट कार्यालय' : 'Divisional / Group Office'}
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <th scope="col" className="table-header serial-col">
                          {isMarathi() ? 'अ. क्र.' : 'Sr. No.'}
                        </th>
                        <th scope="col" className="table-header name-col">
                          {isMarathi() ? 'नाव' : 'Name'}
                        </th>
                        <th scope="col" className="table-header designation-col">
                          {isMarathi() ? 'पद' : 'Designation'}
                        </th>
                        <th scope="col" className="table-header address-col">
                          {isMarathi() ? 'कार्यालयाचा पत्ता' : 'Office Address'}
                        </th>
                        <th scope="col" className="table-header phone-col">
                          {isMarathi() ? 'दूरध्वनी क्रमांक' : 'Phone Number'}
                        </th>
                        <th scope="col" className="table-header email-col">
                          {isMarathi() ? 'ई-मेल' : 'E-Mail'}
                        </th>
                      </tr>
                      <tr className="table-subhead">
                        <td colSpan="6" className="subhead-cell">
                          {isMarathi() ? 'विभागीय कार्यालय, मुंबई' : 'Divisional Office, Mumbai'}
                        </td>
                      </tr>
                      
                      {filteredDivisionalOfficials.map((official, index) => (
                        <tr key={official.id} className={`table-row ${index % 2 === 0 ? 'even-row' : 'odd-row'}`}>
                          <td className="table-cell serial-cell">
                            {isMarathi() ? 
                              (official.serial_number_marathi || convertToMarathiNumerals(index + 1)) : 
                              (official.serial_number_english || index + 1)
                            }
                          </td>
                          <td className="table-cell name-cell">
                            <div className="name-content">
                              <div className="name-text">
                                {isMarathi() ? official.name_marathi : official.name_english}
                              </div>
                            </div>
                          </td>
                          <td className="table-cell designation-cell">
                            <div className="designation-text">
                              {isMarathi() ? official.designation_marathi : official.designation_english}
                            </div>
                          </td>
                          <td className="table-cell address-cell">
                            <div className="address-content">
                              {official.office_address_english ? (
                                <div className="address-text">
                                  {isMarathi() ? official.office_address_marathi : official.office_address_english}
                                </div>
                              ) : (
                                <div className="address-text">
                                  {isMarathi() ? 'वरीलप्रमाणे' : 'Same as above'}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="table-cell phone-cell">
                            {official.phone_numbers_english ? (
                              <button 
                                className="phone-action"
                                onClick={() => handlePhoneClick(official.phone_numbers_english)}
                                onKeyDown={(e) => handleKeyPress(e, () => handlePhoneClick(official.phone_numbers_english))}
                                aria-label={`${isMarathi() ? 'फोन करा' : 'Call'} ${isMarathi() ? official.phone_numbers_marathi : official.phone_numbers_english}`}
                                tabIndex="0"
                                title={isMarathi() ? 'फोन करा' : 'Click to call'}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" fill="currentColor"/>
                                </svg>
                                <span className="phone-text">
                                  {isMarathi() ? official.phone_numbers_marathi : official.phone_numbers_english}
                                </span>
                              </button>
                            ) : (
                              <span className="no-data">-</span>
                            )}
                          </td>
                          <td className="table-cell email-cell">
                            {(() => {
                              const email = official.email || generateDivisionalEmail(official);
                              return email ? (
                                <button 
                                  className="email-action"
                                  onClick={() => handleEmailClick(email)}
                                  onKeyDown={(e) => handleKeyPress(e, () => handleEmailClick(email))}
                                  aria-label={`${isMarathi() ? 'ईमेल पाठवा' : 'Send Email'} ${email}`}
                                  tabIndex="0"
                                  title={isMarathi() ? 'ईमेल पाठवा' : 'Click to email'}
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" fill="currentColor"/>
                                  </svg>
                                  <span className="email-text">{email}</span>
                                </button>
                              ) : (
                                <span className="no-data">-</span>
                              );
                            })()}
                          </td>
                        </tr>
                      ))}
                    </>
                  )}
                </tbody>
              </table>
            ) : (
              <div className="no-results">
                <div className="no-results-icon">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" fill="currentColor"/>
                  </svg>
                </div>
                <p>{isMarathi() ? 'कोणतेही अधिकारी सापडले नाहीत' : 'No officials found'}</p>
              </div>
            )}
          </div>
        </section>
        */}

        {/* Office Hours Widget */}
        <section className="hours-section" aria-labelledby="hours-title" data-read-section="contact-sop-content">
          <h2 id="hours-title" className="section-title">
            {isMarathi() ? 'कार्यालयीन वेळ' : 'Office Hours'}
          </h2>
          <div className="hours-widget">
            <div className="hours-info">
              <div className="hours-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" fill="currentColor"/>
                </svg>
              </div>
              <div className="hours-content">
                <h3>{isMarathi() ? 'कार्यालयीन वेळ' : 'Office Hours'}</h3>
                
                {contactTimings.length > 0 ? (
                  contactTimings.map((timing) => (
                    <div key={timing.id} className="timing-item">
                      <h4 className="timing-office-type">
                        {isMarathi() ? timing.office_type_marathi : timing.office_type_english}
                      </h4>
                      <p className="hours-time">
                        {isMarathi() ? timing.timings_marathi : timing.timings_english}
                      </p>
                      <p className="hours-weekly-off">
                        <strong>{isMarathi() ? 'साप्ताहिक सुट्टी: ' : 'Weekly Off: '}</strong>
                        {isMarathi() ? timing.weekly_off_marathi : timing.weekly_off_english}
                      </p>
                    </div>
                  ))
                ) : (
                  <div className="timing-item">
                    <p className="hours-time">
                      {isMarathi() ? 'सकाळी ९.४५ ते सायंकाळी ५.३० वा.' : '9:45 AM to 5:30 PM'}
                    </p>
                    <p className="hours-weekly-off">
                      <strong>{isMarathi() ? 'साप्ताहिक सुट्टी: ' : 'Weekly Off: '}</strong>
                      {isMarathi() ? 'शनिवार, रविवार' : 'Saturday, Sunday'}
                    </p>
                  </div>
                )}
                
                <p className="hours-note">
                  {isMarathi() 
                    ? 'सार्वजनिक सुट्ट्यांमध्ये कार्यालय बंद असते' 
                    : 'Office remains closed on public holidays'
                  }
                </p>
              </div>
            </div>
          </div>
        </section>

      </div>
    </div>
  );
};

export default ContactSOP;

